import { AxiosResponse } from 'axios';
import { environment } from '@environment';
import { ErrorCodeContentDTO } from 'core/models/product/fix-error.dto';
import { HardwareDTO } from 'core/models/product/hardware.dto';
import { PackagesDTO } from 'core/models/product/packages.dto';
import { EndpointHelper } from 'core/services/endpoint.helper';
import { HttpClient } from 'core/services/http';

export type ProductProperties =
  | 'Name'
  | 'ProductImage'
  | 'ProductName'
  | 'Code'
  | 'Price'
  | 'Logo'
  | 'PackageColor'
  | 'AdditionalProductImage'
  | 'Description'
  | 'ProductDetail'
  | 'ProductSummary'
  | 'SecondaryPackageColor'
  | 'Features'
  | 'Channels'
  | 'IconCode'
  | 'SelfServiceMobileBackgroundImage'
  | 'SelfServiceProductCardImage'
  | 'SelfServiceLogoImage'
  | 'SelfServiceLogoImageDark'
  | 'SelfServiceLogoImageLight'
  | 'SelfServiceBackgroundImage'
  | 'SelfServiceProductColor'
  | 'SelfServiceProductTextColor'
  | 'SelfServiceProductHeading'
  | 'SelfServiceProductDescription'
  | 'SelfServiceProductSummary'
  | 'SelfServiceProductContent'
  | 'SelfServiceMobileBackgroundPattern'
  | 'SelfServiceBackgroundPattern'
  | 'SelfServiceProductCodes'
  | 'SelfServicePromoBackgroundImage'
  | 'SelfServiceProductCardCTA'
  | 'SelfServiceProductDetailCTA'
  | 'SelfServiceIconCode'
  | 'FeaturedChannels';

export type HardwareProperties =
  | 'AdditionalProductImage'
  | 'Code'
  | 'Description'
  | 'Features'
  | 'IconCode'
  | 'Price'
  | 'ProductDetail'
  | 'SelfServiceProductImage'
  | 'SelfServiceMainImage'
  | 'SelfServiceBackgroundImage'
  | 'SelfServiceProductCardImage'
  | 'SelfServiceLogoImageLight'
  | 'SelfServiceProductHeading'
  | 'ProductName'
  | 'ProductSummary'
  | 'FeatureHeadline'
  | 'Specifications'
  | 'Capabilities'
  | 'Manual'
  | 'SelfServiceProductCodes'
  | 'SelfServiceProductSummary'
  | 'SelfServiceIconCode'
  | 'SelfServiceInsurancePrice'
  | 'SelfServiceInsurancePriceTerm'
  | 'SelfServicePromoBackgroundImage'
  | 'SelfServiceInsuranceLabel';

export type ServicesProperties =
  | 'AdditionalProductImage'
  | 'Code'
  | 'Description'
  | 'Features'
  | 'IconCode'
  | 'Price'
  | 'ProductDetail'
  | 'SelfServiceProductImage'
  | 'SelfServiceMainImage'
  | 'SelfServiceBackgroundImage'
  | 'SelfServiceProductCardImage'
  | 'ProductName'
  | 'ProductSummary'
  | 'FeatureHeadline'
  | 'Specifications'
  | 'Capabilities'
  | 'Manual'
  | 'SelfServiceProductCodes'
  | 'SelfServiceIconCode'
  | 'SelfServiceInsurancePrice'
  | 'SelfServiceInsurancePriceTerm'
  | 'SelfServiceProductSummary'
  | 'SelfServicePromoBackgroundImage'
  | 'SelfServiceCoverAmount'
  | 'SelfServiceMonthlyPrice'
  | 'SelfServiceAgeGroup'
  | 'SelfServiceInsuranceLabel';

export type InternetProperties =
  | 'SelfServiceProductHeading'
  | 'SelfServiceProductCardImage'
  | 'SelfServiceProductCodes'
  | 'SelfServiceProductDescription'
  | 'SelfServiceProductImage'
  | 'SelfServiceProductColor'
  | 'SelfServiceProductSummary'
  | 'SelfServiceMainImage'
  | 'SelfServiceBackgroundImage'
  | 'SelfServiceMobileBackgroundImage'
  | 'SelfServiceBackgroundPattern'
  | 'SelfServiceMobileBackgroundPattern';

export type ErrorCodeProperties =
  | 'ErrorCode'
  | 'IsClearable'
  | 'Cause'
  | 'CauseImageLink'
  | 'CauseImageCaption'
  | 'CauseVideoLink'
  | 'Fix'
  | 'FixImageLink'
  | 'FixImageCaption'
  | 'FixVideoLink'
  | 'FixErrorOnlineLink';

export type RewardProperties =
  | 'RewardId'
  | 'Title'
  | 'Icon'
  | 'Type'
  | 'ActionLink'
  | 'Logo'
  | 'BackgroundImage'
  | 'CompletedImage'
  | 'PromotionCode'
  | 'PrinciplePackage'
  | 'StartDate'
  | 'EndDate'
  | 'LabelIcon'
  | 'TagLabelLead'
  | 'TagLabel'
  | 'Descriptor'
  | 'DescriptorMobile'
  | 'TagLine'
  | 'BodyText'
  | 'ErrorMessage'
  | 'AcceptedMessage'
  | 'ClaimedMessage'
  | 'TermsAndConditions'
  | 'AcceptedButton'
  | 'RewardLevel';

export type WinnersCircleProperties = 'Header' | 'SectionTitle' | 'Image' | 'ImageRight' | 'Body' | 'ButtonText';

export type InsuranceLandingProperties =
  | 'Header'
  | 'SectionTitle'
  | 'Image'
  | 'ImageRight'
  | 'Body'
  | 'ButtonText'
  | 'PrimaryButton'
  | 'SecondaryButton';

export type ChatSettings = 'WhatsAppLink';

export enum ContentType {
  Packages,
  PackagesPT,
  Hardware,
  Rewards,
  EarnStars,
  WinnersCircle,
  ErrorCodes,
  Services,
  Insurance,
  InsuranceTabs,
  Internet,
  Safety,
}

export const ContentLocations = {
  [ContentType.Packages]: 'products/packages',
  [ContentType.PackagesPT]: 'products/pacotes',
  [ContentType.Hardware]: 'products/hardware',
  [ContentType.Rewards]: 'rewards/reward-promos',
  [ContentType.EarnStars]: 'rewards/earn-stars',
  [ContentType.WinnersCircle]: 'rewards/winners-circle',
  [ContentType.ErrorCodes]: 'error-codes',
  [ContentType.Services]: 'products/services',
  [ContentType.Insurance]: 'insurance/insurance-landing',
  [ContentType.Safety]: 'namola/namola-landing',
  [ContentType.InsuranceTabs]: 'insurance/insurance-tabs',
  [ContentType.Internet]: 'products/internet',
};

export const ContentChildren = {
  [ContentType.Packages]: 'Packages',
  [ContentType.PackagesPT]: 'Packages',
  [ContentType.Hardware]: 'Hardware',
  [ContentType.ErrorCodes]: 'ErrorCode',
  [ContentType.Rewards]: 'RewardPromos',
  [ContentType.WinnersCircle]: 'ImageDescriptionComponent',
  [ContentType.EarnStars]: 'EarnStars',
  [ContentType.Services]: 'Services',
  [ContentType.Insurance]: 'ImageDescriptionComponent',
  [ContentType.Safety]: 'ImageDescriptionComponent',
  [ContentType.InsuranceTabs]: 'ImageDescriptionComponent',
  [ContentType.Internet]: 'Internet',
};

export const MediaResolutions = {
  [ContentType.Packages]: [
    'Logo',
    'ProductImage',
    'AdditionalProductImage',
    'SelfServiceProductImage',
    'SelfServiceMainImage',
    'SelfServiceBackgroundImage',
    'SelfServiceMobileBackgroundImage',
    'SelfServiceBackgroundPattern',
    'SelfServiceMobileBackgroundPattern',
    'SelfServiceProductCardImage',
    'SelfServiceLogoImage',
    'SelfServiceLogoImageDark',
    'SelfServiceLogoImageLight',
    'SelfServicePromoBackgroundImage',
  ],
  [ContentType.PackagesPT]: [
    'Logo',
    'ProductImage',
    'AdditionalProductImage',
    'SelfServiceProductImage',
    'SelfServiceMainImage',
    'SelfServiceBackgroundImage',
    'SelfServiceMobileBackgroundImage',
    'SelfServiceBackgroundPattern',
    'SelfServiceMobileBackgroundPattern',
    'SelfServiceProductCardImage',
    'SelfServiceLogoImage',
    'SelfServiceLogoImageDark',
    'SelfServiceLogoImageLight',
    'SelfServicePromoBackgroundImage',
  ],
  [ContentType.Hardware]: [
    'Logo',
    'ProductImage',
    'AdditionalProductImage',
    'SelfServiceProductImage',
    'SelfServiceMainImage',
    'SelfServiceBackgroundImage',
    'SelfServiceMobileBackgroundImage',
    'SelfServiceBackgroundPattern',
    'SelfServiceMobileBackgroundPattern',
    'SelfServiceProductCardImage',
    'SelfServiceLogoImage',
    'SelfServiceLogoImageDark',
    'SelfServiceLogoImageLight',
    'SelfServicePromoBackgroundImage',
  ],
  [ContentType.Services]: [
    'Logo',
    'ProductImage',
    'AdditionalProductImage',
    'SelfServiceProductImage',
    'SelfServiceMainImage',
    'SelfServiceBackgroundImage',
    'SelfServiceMobileBackgroundImage',
    'SelfServiceBackgroundPattern',
    'SelfServiceMobileBackgroundPattern',
    'SelfServiceProductCardImage',
    'SelfServiceLogoImage',
    'SelfServiceLogoImageDark',
    'SelfServiceLogoImageLight',
    'SelfServicePromoBackgroundImage',
  ],
  [ContentType.Internet]: [
    'SelfServiceProductCardImage',
    'SelfServiceProductImage',
    'SelfServiceBackgroundImage',
    'SelfServiceMobileBackgroundImage',
    'SelfServiceBackgroundPattern',
    'SelfServiceMobileBackgroundPattern',
  ],
  [ContentType.ErrorCodes]: ['Logo'],
  [ContentType.Rewards]: ['Logo', 'BackgroundImage', 'CompletedImage', 'PrinciplePackage'],
  [ContentType.WinnersCircle]: ['Image'],
  [ContentType.Insurance]: ['Image'],
  [ContentType.Safety]: ['Image'],
  [ContentType.InsuranceTabs]: ['Image'],
  [ContentType.EarnStars]: ['Logo', 'BackgroundImage', 'CompletedImage', 'PrinciplePackage'],
};

export const IdResolutions = {
  [ContentType.Packages]: ['FeaturedChannels'],
  [ContentType.PackagesPT]: ['FeaturedChannels'],
  [ContentType.Hardware]: [''],
  [ContentType.Services]: [''],
  [ContentType.Internet]: [''],
  [ContentType.ErrorCodes]: [''],
  [ContentType.Rewards]: [''],
  [ContentType.WinnersCircle]: [''],
  [ContentType.Insurance]: [''],
  [ContentType.Safety]: [''],
  [ContentType.InsuranceTabs]: [''],
  [ContentType.EarnStars]: [''],
};

export const ContentRes = {
  [ContentType.Packages]: ['Features'],
  [ContentType.Rewards]: ['BodyText', 'ErrorMessage', 'AcceptedMessage', 'ClaimedMessage'],
};

export interface ContentDTO {
  properties: PackagesDTO | HardwareDTO | ErrorCodeContentDTO;
}

export class UmbracoContentService {
  private static apiBuilder = new EndpointHelper(`${environment.UMBRACO_BASE_URI}`);

  static getContent(
    contentType: ContentType,
    select?: (
      | ProductProperties
      | HardwareProperties
      | ErrorCodeProperties
      | RewardProperties
      | ServicesProperties
      | InternetProperties
      | WinnersCircleProperties
      | ChatSettings
      | InsuranceLandingProperties
    )[],
    locale = ''
  ): Promise<ContentDTO[]> {
    const url = this.apiBuilder
      .createUrl(ContentLocations[contentType])
      .addBaseRouteParam('global-site-configuration', locale)
      .addRouteParam('ujson')
      .addQueryParam('children', ContentChildren[contentType])
      .addQueryParam('select', select?.join(','))
      .addQueryParam('resolveMedia', select?.filter((prop: any) => MediaResolutions[contentType]?.includes(prop))?.join(',') || undefined)
      .addQueryParam('resolveToIds', select?.filter((prop: any) => IdResolutions[contentType]?.includes(prop))?.join(',') || undefined)
      .addQueryParam('noCache', true)
      // .addQueryParam('resolvecontent', select?.filter(prop => ContentRes[contentType]?.includes(prop))?.join(',') || undefined)
      .build();

    return HttpClient.GetInstance.get<any>(url).then((res: AxiosResponse<any>) => (res?.data?.entities ? res.data.entities : [res?.data]));
  }
}